import { openCreateFinancialDeckModal } from "@/components/CreateFinancialDeckModal/openCreateFinancialDeckModal";
import { dealModel } from "@/models/deal";
import { financialDeckModel } from "@/models/financialDecks";
import { userIsCreatorConstraint } from "@/orm/model";
import { Route } from "@/paths";
import { CustomCellRendererProps } from "@ag-grid-community/react";
import { Button, Group, Menu, Stack } from "@mantine/core";
import { useAtomValue } from "jotai";
import { BiPlus } from "react-icons/bi";
import { BsThreeDots } from "react-icons/bs";
import { LuFileEdit, LuFileSpreadsheet } from "react-icons/lu";
import { RiPushpin2Fill } from "react-icons/ri";
import { Link, generatePath } from "react-router-dom";

export const ModelRenderer = (props: CustomCellRendererProps) => {
  const isEditable = !!props.api.getGridOption("defaultColDef")?.editable;
  const dealEntity = dealModel.createEntityInstance({ id: props.node.data.id });
  const {
    entities: deckEntites,
    loading,
    error,
  } = useAtomValue(
    dealEntity.getObserveManyBelongingsAtom(
      financialDeckModel,
      "belongsToDealIds",
      {
        constraints: [userIsCreatorConstraint()],
      },
    ),
  );

  const sortedDeckEntites = deckEntites?.sort((a, b) => {
    return (
      (b.data.createdAt?.toMillis() ?? 0) - (a.data.createdAt?.toMillis() ?? 0)
    );
  });

  const pinnedModel = deckEntites?.find(
    (entity) => entity.id === props.node.data.pinnedModelId,
  );

  const editModelId = pinnedModel?.id || deckEntites?.[0]?.id;

  if (loading) {
    return null;
  }

  if (error) {
    console.log(error);
    return <div>Error</div>;
  }

  if (!deckEntites || (deckEntites || []).length === 0) {
    return (
      <Stack w={"100%"} h={"100%"} align="center" justify="center">
        <Button
          w={"100%"}
          size="xs"
          leftSection={<LuFileSpreadsheet size={16} />}
          onClick={() => openCreateFinancialDeckModal(dealEntity)}
          fz={13}
          justify="space-between"
          rightSection={<LuFileSpreadsheet size={16} opacity={0} />}
          disabled={!isEditable}
        >
          Create Model
        </Button>
      </Stack>
    );
  }

  return (
    <Stack w={"100%"} h={"100%"} align="center" justify="center">
      <Button.Group w={"100%"} bg="white">
        <Button
          justify="space-between"
          w={"100%"}
          size="xs"
          variant="light"
          leftSection={<LuFileEdit size={16} />}
          autoContrast
          color="accent"
          component={Link}
          to={generatePath(Route.ModelTab, {
            modelId: editModelId!,
            modelTabId: "historicals",
          })}
          fz={13}
          target="_blank"
        >
          {isEditable ? "Edit" : "View"} Model
        </Button>
        <Menu
          shadow="md"
          width={200}
          position="bottom-end"
          withArrow
          arrowPosition="center"
        >
          <Menu.Target>
            <Button
              size="xs"
              variant="filled"
              color="blue.1"
              autoContrast
              pl="xs"
              pr="xs"
              c={"blue.8"}
            >
              <BsThreeDots size={18} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {sortedDeckEntites?.map((deck) => (
              <Menu.Item
                key={deck.id}
                onClick={() => {
                  window.open(
                    generatePath(Route.ModelTab, {
                      modelId: deck.id,
                      modelTabId: "historicals",
                    }),
                    "_blank",
                    "noreferrer",
                  );
                }}
              >
                <Group gap={"xs"} align="center" justify="space-between">
                  {deck.data.name}
                  {deck.id === props.node.data.pinnedModelId && (
                    <RiPushpin2Fill style={{ position: "relative", top: -2 }} />
                  )}
                </Group>
              </Menu.Item>
            ))}
            <Menu.Divider />
            <Menu.Item
              leftSection={<BiPlus />}
              onClick={() => openCreateFinancialDeckModal(dealEntity)}
              disabled={!isEditable}
            >
              Create Model
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Button.Group>
    </Stack>
  );
};
